/**
 * Runway configuration: which runway arrivals and departures use, the approach in use, and the
 * wind components against the published tailwind limits. The preferred runways come from the
 * airport file; the game does not change runways with the wind yet (docs/sim-design.md).
 */
import { findRunwayEnd, type AirportData, type InstrumentApproach, type RunwayEnd } from './airport';
import { defaultApproach } from './approach';

/** Tailwind limit when the airport file has no runway-use table, knots (a common preferential-runway value). */
export const DEFAULT_MAX_TAILWIND_KT = 5;

/** Headwind (negative = tailwind) and crosswind (positive = from the right) for a wind blowing FROM `directionDeg`, true. */
export function windComponents(end: RunwayEnd, wind: { directionDeg: number; speedKt: number }): { headwindKt: number; crosswindKt: number } {
  const rel = ((wind.directionDeg - end.headingDeg) * Math.PI) / 180;
  return { headwindKt: wind.speedKt * Math.cos(rel), crosswindKt: wind.speedKt * Math.sin(rel) };
}

export interface RunwayConfig {
  arrival: { runway: string; runways: string[]; approach?: InstrumentApproach };
  departure: { runway: string; runways: string[] };
  /** Tailwind component in knots; 0 or less means a headwind. */
  tailwindKt: { arrival: number; departure: number };
  maxTailwindKt: { arrival: number; departure: number };
  overLimit: { arrival: boolean; departure: boolean };
}

export function runwayConfig(airport: AirportData, wind: { directionDeg: number; speedKt: number } = airport.wind, active?: { arrivals?: readonly string[]; departures?: readonly string[] }): RunwayConfig {
  const use = airport.xAtc.runwayUse;
  const tail = (name: string) => { const e = findRunwayEnd(airport, name); return e ? -windComponents(e, wind).headwindKt : 0; };
  const limit = (list: { runway: string; maxTailwindKt: number }[] | undefined, name: string) => list?.find((u) => u.runway === name)?.maxTailwindKt ?? DEFAULT_MAX_TAILWIND_KT;
  const valid = new Set(airport.runways.flatMap((r) => r.ends.map((e) => e.name)));
  const clean = (xs: readonly string[] | undefined, fallback: string) => {
    const selected = [...new Set((xs ?? []).filter((x) => valid.has(x)))];
    return selected.length ? selected : [fallback];
  };
  const arrivals = clean(active?.arrivals, airport.arrivalRunway), departures = clean(active?.departures, airport.departureRunway);
  const arr = arrivals[0]!, dep = departures[0]!;
  const tailwindKt = { arrival: tail(arr), departure: tail(dep) };
  const maxTailwindKt = { arrival: limit(use?.arrival, arr), departure: limit(use?.departure, dep) };
  const approach = defaultApproach(airport, arr);
  return {
    arrival: { runway: arr, runways: arrivals, ...(approach ? { approach } : {}) },
    departure: { runway: dep, runways: departures },
    tailwindKt,
    maxTailwindKt,
    overLimit: { arrival: tailwindKt.arrival > maxTailwindKt.arrival, departure: tailwindKt.departure > maxTailwindKt.departure },
  };
}
